import { useCallback, useEffect } from 'react';
import type { PlaybackClock } from '../../animation/clock';
import { displayObject, poseOf, type DisplayObject } from '../../animation/timeline';
import * as A from '../../domain/animationOps';
import * as S from '../../domain/sceneOps';
import { POSE_KEYS, type Animation, type Project, type Scene } from '../../domain/schemas';
import { useObservable } from '../../lib/useObservable';
import { useServices } from '../../state/services';
import type { EditFn } from './useSceneEdit';

/**
 * The animation shown on the timeline: the selected one if it belongs to the active
 * scene, otherwise the scene's first animation, otherwise none (base properties).
 */
export function useActiveAnimation(project: Project, scene: Scene): Animation | null {
  const { selection } = useServices();
  const selectedId = useObservable(selection).animationId;
  const animations = project.animations.filter((a) => a.sceneId === scene.id);
  const active = animations.find((a) => a.id === selectedId) ?? animations[0] ?? null;
  // Keep the explicit selection in sync (prompts use it as "the current animation").
  useEffect(() => {
    if ((active?.id ?? null) !== selectedId) selection.selectAnimation(active?.id ?? null);
  }, [active?.id, selectedId, selection]);
  return active;
}

/**
 * Keeps the clock in step with the animation on the timeline (see `PlaybackClock.load`):
 * selecting a different animation — in this scene or another — starts it at frame 0;
 * editing the selected one's duration, fps or loop keeps the playhead; re-rendering with
 * the same animation changes nothing. The play state is kept across both.
 */
export function useClockFollowsAnimation(clock: PlaybackClock, active: Animation | null): void {
  const id = active?.id ?? null;
  const fps = active?.fps;
  const durationFrames = active?.durationFrames;
  const loop = active?.loop;
  useEffect(() => {
    clock.load(id, fps !== undefined && durationFrames !== undefined && loop !== undefined ? { fps, durationFrames, loop } : null);
  }, [clock, id, fps, durationFrames, loop]);
}

/**
 * A transform or pose edit of objects, expressed as wanted values computed from the
 * object as currently *displayed* (animated values at the playhead applied).
 *
 * - Auto-Key on (and an animation selected): the edit becomes keyframes at the playhead
 *   (see `autoKeyEdit`).
 * - Otherwise it edits base properties. `reference: 'shown'` (canvas drags, nudges, quick
 *   buttons) applies the *change* to the base, so dragging an animated object moves its
 *   base by the same amount; `reference: 'base'` (inspector fields showing base values)
 *   sets the values directly.
 * Returns false (and shows the error) when the edit is refused. One call = one undo step.
 */
export type TransformEditFn = (
  label: string,
  ids: readonly string[],
  make: (shown: DisplayObject) => A.TransformEdit,
  coalesceKey?: string,
  reference?: 'shown' | 'base',
) => boolean;

export function useTransformEdit(edit: EditFn, sceneId: string, animation: Animation | null, onNotice: (message: string | null) => void): TransformEditFn {
  const { animation: bus } = useServices();
  return useCallback<TransformEditFn>(
    (label, ids, make, coalesceKey, reference = 'shown') => {
      const autoKey = bus.autoKey.get() && !!animation;
      const frame = bus.clock.frame();
      const overridden = new Set<string>();
      const ok = edit(
        label,
        (p) => {
          let q = p;
          const anim = animation ? q.animations.find((a) => a.id === animation.id) ?? null : null;
          for (const id of ids) {
            const scene = S.sceneOf(q, sceneId);
            const base = S.objectOf(scene, id);
            const values = anim ? A.valuesAt(anim, id, frame) : undefined;
            const shown = displayObject(base, values);
            if (autoKey && anim) {
              q = A.autoKeyEdit(q, anim.id, id, frame, make(shown)).project;
              continue;
            }
            const wanted = make(reference === 'base' ? base : shown);
            const e = reference === 'base' ? wanted : relativeToBase(base, shown, wanted);
            q = A.applyBaseEdit(q, sceneId, base, e);
            // Tell the user when the animation will keep overriding what they just changed.
            if (anim) for (const c of Object.keys(A.editChannels(base, {}, e))) if (A.animatedChannels(anim, id).includes(c as never)) overridden.add(A.CHANNEL_LABELS[c as keyof typeof A.CHANNEL_LABELS]);
          }
          return q;
        },
        coalesceKey,
      );
      if (ok) {
        onNotice(
          overridden.size && animation
            ? `Changed the base ${[...overridden].join(', ')}, but “${animation.name}” animates ${overridden.size === 1 ? 'it' : 'them'}, so the canvas keeps showing the animated value. Turn on Auto-Key to edit keyframes instead.`
            : null,
        );
      }
      return ok;
    },
    [bus, edit, sceneId, animation, onNotice],
  );
}

/** Converts a wanted displayed change into the same change of the base object. */
function relativeToBase(base: DisplayObject, shown: DisplayObject, wanted: A.TransformEdit): A.TransformEdit {
  const out: A.TransformEdit = {};
  if (wanted.x !== undefined) out.x = base.x + (wanted.x - shown.x);
  if (wanted.y !== undefined) out.y = base.y + (wanted.y - shown.y);
  if (wanted.width !== undefined) out.width = shown.width > 0 ? (base.width * wanted.width) / shown.width : wanted.width;
  if (wanted.height !== undefined) out.height = shown.height > 0 ? (base.height * wanted.height) / shown.height : wanted.height;
  if (wanted.rotation !== undefined) out.rotation = base.rotation + (wanted.rotation - shown.rotation);
  if (wanted.opacity !== undefined) out.opacity = Math.min(1, Math.max(0, base.opacity + (wanted.opacity - shown.opacity)));
  if (wanted.visible !== undefined) out.visible = wanted.visible;
  if (wanted.pose) {
    const b = poseOf(base);
    const s = shown.pose ?? b;
    out.pose = {};
    for (const k of POSE_KEYS) if (wanted.pose[k] !== undefined) out.pose[k] = b[k] + (wanted.pose[k]! - s[k]);
  }
  return out;
}
